import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, oneOf } from 'prop-types'

import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  addAdvancedFilter,
  removeAdvancedFilter,
  setPersonalFilter,
  setPurchaseFilter,
} from 'Pods/SearchPage/Store/actions/moreFilters'
import { PURCHASE_FILTER } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import {
  ALL_MODELS,
  getGeneralFiltersConfig,
  getMyLibraryFiltersConfig,
  getPurchaseFiltersConfig,
  SECTION,
} from './constants'

const PURCHASED_ONLY_SELECTED = [PURCHASE_FILTER.PURCHASED_MODELS_ONLY]

// hideGeneralLabel drops only the General group's title when an external title already names the
// section (mobile collapsible header). The My Library label stays as a sub-heading.
// section splits the groups for the More filters columns, where My library (Status) and General
// (Other) sit in separate sections (Figma 3183:36396).
function AdvancedFiltersContent({ hideGeneralLabel = false, section = SECTION.ALL }) {
  const translate = useTranslate()

  const advancedFilters = useSearchPageStore(state => state.advancedFilters ?? EMPTY_ARRAY)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  // Personal catalog filters (wishlist, purchased) are backed by ItemsSearch filters that no-op
  // without current_user, so a signed-out viewer would see an "active" filter over unfiltered
  // results. Hidden until pageConfig confirms a logged-in user (client-side; SSR is user-blind).
  const pageConfig = usePageConfig()
  const isLoggedIn = !!pageConfig?.currentUserId
  const trackFilterSelect = useTrackFilterSelect()

  const handleGeneralFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      trackFilterSelect(FILTER_NAME.ADVANCED, value)
      addAdvancedFilter(value)
    } else {
      removeAdvancedFilter(value)
    }
  }, [trackFilterSelect])

  const handleMyLibraryFilterChange = useCallback((_, value) => {
    if (value !== ALL_MODELS) {
      trackFilterSelect(FILTER_NAME.PERSONAL, value)
    }
    setPersonalFilter(value === ALL_MODELS ? undefined : value)
  }, [trackFilterSelect])

  const handlePurchaseFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      trackFilterSelect(FILTER_NAME.PURCHASES, value)
    }
    setPurchaseFilter(isOn ? value : undefined)
  }, [trackFilterSelect])

  const selectedMyLibraryFilter = useMemo(() => [personalFilter ?? ALL_MODELS], [personalFilter])

  const generalConfig = useMemo(() => {
    const config = getGeneralFiltersConfig(translate)
    return hideGeneralLabel ? [{ ...config[0], label: '' }] : config
  }, [hideGeneralLabel, translate])

  const myLibraryConfig = useMemo(() => getMyLibraryFiltersConfig(translate), [translate])
  const purchaseConfig = useMemo(() => getPurchaseFiltersConfig(translate), [translate])

  const showGeneral = section !== SECTION.MY_LIBRARY
  const showMyLibrary = section !== SECTION.GENERAL && isLoggedIn

  return (
    <>
      {showGeneral && (
        <FiltersSidebar
          isCollapsible={false}
          filters={generalConfig}
          selectedFilters={advancedFilters}
          onFiltersChange={handleGeneralFilterChange}
          isControlled
          useNewCheckboxes
          hideLastDivider={!showMyLibrary}
          hideShowMore
          baseFormWrapperClassName="filter-wrapper__content"
        />
      )}
      {showMyLibrary && (
        <>
          <FiltersSidebar
            isCollapsible={false}
            filters={myLibraryConfig}
            selectedFilters={selectedMyLibraryFilter}
            onFiltersChange={handleMyLibraryFilterChange}
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
            useRadio
          />
          <FiltersSidebar
            isCollapsible={false}
            filters={purchaseConfig}
            selectedFilters={purchaseFilter === PURCHASE_FILTER.PURCHASED_MODELS_ONLY
              ? PURCHASED_ONLY_SELECTED
              : EMPTY_ARRAY}
            onFiltersChange={handlePurchaseFilterChange}
            isControlled
            useNewCheckboxes
            hideLastDivider
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
          />
        </>
      )}
    </>
  )
}

AdvancedFiltersContent.propTypes = {
  hideGeneralLabel: bool,
  section: oneOf(Object.values(SECTION)),
}

export default memo(AdvancedFiltersContent)
